import * as React from 'react';
import { useEffect, useState } from 'react';
import { rng, WorkgroupListing } from '../../amorg-common';
import { ProtectedPageWrapper } from '../../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { OrgChart } from './_components/OrgChart';
import { PublicAmorgUser } from '../../amorg-common';
import { OrganizationOwner } from '../../amorg-common';
import OrgOwnerEditor from '../../features/organizationSettings/OrgOwnerEditor';
import { WorkgroupEditor } from './_components/WorkgroupEditor';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { Button } from '../../designSystem/button/Button';
import { getOwner } from '../../api/organizationClientApi';
import { getUsers } from '../../api/userClientApi';
import { getWorkgroups, createWorkgroup } from '../../api/workgroupClientApi';
import { Modal } from '../../designSystem/Modal';
import { PageHeader } from '../../designSystem/PageHeader';

type WorkgroupListPageState = {
    allUsers: PublicAmorgUser[];
    workgroupList: WorkgroupListing[];
    inEditWorkgroup: WorkgroupListing | null;

    organizationOwner: OrganizationOwner | null;
    inEditOrganizationOwner: OrganizationOwner | null;
}
export function OrganizationPage(): JSX.Element {

    const [state, setState] = useState<WorkgroupListPageState>(
        {
            workgroupList: [],
            allUsers: [],
            inEditWorkgroup: null,
            organizationOwner: null,
            inEditOrganizationOwner: null
        });

    const [editorOpen, setEditorOpen] = useState(false);

    useEffect(() => {
        const getData = async () => {
            const allUsers = await getUsers();
            const workgroupList = await getWorkgroups();
            const organizationOwner = await getOwner();
            setState(
                {
                    ...state,
                    allUsers,
                    workgroupList,
                    organizationOwner
                }
            );
        };
        getData();
    }, []);

    async function handleClickCreateNewWorkgroup() {

        const id = rng.getRandomId('wg_');

        const createResult = await createWorkgroup(id, 'Ny arbejdsgruppe');

        if (createResult.isSuccess) {

            setEditorOpen(true)

            const allWorkgroups = await getWorkgroups();
            const selectedWorkgroup = allWorkgroups.find(wg => wg.id === id) || null;
            setState(
                {
                    ...state,
                    workgroupList: allWorkgroups,
                    inEditWorkgroup: selectedWorkgroup
                });
        }
    }

    function handleClickEditWorkgroup(workgroupId: string) {

        setEditorOpen(true);
        setState(
            {
                ...state,
                inEditWorkgroup: state.workgroupList.find(wg => wg.id === workgroupId) || null
            });
    }

    function handleClickEditOwnerGroup() {

        if (state.organizationOwner === null) {
            setState({
                ...state,
                inEditOrganizationOwner: {
                    firstname: '', middlename: '', lastname: ''
                }
            })
        }
        else {
            setState({
                ...state,
                inEditOrganizationOwner: { ...state.organizationOwner }
            })
        }
    }

    async function handleWorkgroupUpdated(closeEditor: boolean) {
        const allUsers = await getUsers();
        const workgroupList = await getWorkgroups();
        const inEditWorkgroup = workgroupList.find(wg => wg.id === state.inEditWorkgroup?.id) || null;

        setState(
            {
                ...state,
                allUsers,
                workgroupList,
                inEditWorkgroup: closeEditor ? null : inEditWorkgroup,
            }
        );
    }

    async function handleOrganizationOwnerUpdated(closeEditor: boolean) {
        const organizationOwner = await getOwner();
        setState(
            {
                ...state,
                organizationOwner,
                inEditOrganizationOwner: closeEditor ? null : organizationOwner,
            }
        );
    }

    function handleWorkgroupEditorClose() {
        setState({ ...state, inEditWorkgroup: null })
    }

    function handleOrganizationOwnerEditorClose() {
        setState({ ...state, inEditOrganizationOwner: null });
    }

    return (
        <ProtectedPageWrapper requiredPermissions={['ViewOrganization']} selectedMenuItemKey='workgroups'>
            <SingleColumnLayout
                noDivider
                contentContainer={
                    <>
                        <PageHeader 
                            headerIcon='interface_user_team'
                            headerText="Organisation" headerStyle="secondary-gray"
                            buttons={
                                <Button variant="filled" onClick={handleClickCreateNewWorkgroup} icon={"actions_plus"} text={"Opret ny gruppe"} />
                            } />
                    
                        <OrgChart
                            allWorkgroups={state.workgroupList}
                            organizationOwner={state.organizationOwner}
                            onClickCreateNew={handleClickCreateNewWorkgroup}
                            onClickEditGroup={handleClickEditWorkgroup}
                            onClickEditRootGroup={handleClickEditOwnerGroup}
                        />

                        <Modal 
                            title={"Organisationsansvarlig"}
                            content={
                                state.inEditOrganizationOwner !== null &&
                                <OrgOwnerEditor
                                    organizationOwner={state.inEditOrganizationOwner}
                                    onOwnerUpdated={() => handleOrganizationOwnerUpdated(true)}
                                    onClose={handleOrganizationOwnerEditorClose} />
                            }
                            showModal={state.inEditOrganizationOwner !== null}
                            onCancel={handleOrganizationOwnerEditorClose}/>
                            

                            {state.inEditWorkgroup &&

                                <Modal 
                                    title={"Rediger gruppe"}
                                    content={
                                        <WorkgroupEditor
                                            allUsers={state.allUsers}
                                            allWorkgroups={state.workgroupList}
                                            inEditWorkgroup={state.inEditWorkgroup}
                                            onUpdated={() => handleWorkgroupUpdated(false)}
                                            onDeleted={() => handleWorkgroupUpdated(true)}
                                            onClose={() => handleWorkgroupEditorClose()} />
                                    }
                                    showModal={editorOpen}
                                    onCancel={() => handleWorkgroupEditorClose()}/>

                            }
                    </>
                }
            />
        </ProtectedPageWrapper>
    );
}

